/* eslint-disable @typescript-eslint/no-explicit-any */
import type { JSX } from 'react';
import { Fragment, useEffect } from 'react';

import { getIn } from 'formik';
import styled from 'styled-components';

import { clearContactCardError } from '../../store/fieldError';
import { CONTROL_TYPE } from '../../utils/constants';
import { Control } from '..';
import Label from '../Label';
import { ReadonlyField } from '../ReadonlyField';

interface WrapperProps {
  editMode?: boolean;
  hasError?: boolean;
}

export const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin-bottom: ${({ editMode, hasError }) =>
    editMode || hasError ? 0 : 8}px;
`;

const FieldLabel = styled(Label)`
  color: var(--neutral-500);
  font: var(--sprout-font-body2-bold);
`;

function getValue(name: string, store: any): string {
  return getIn(store.customer, name) || '';
}

function checkIfError(error: any): boolean {
  switch (typeof error) {
    case 'string':
      return true;
    case 'object':
      return error && Object.keys({ ...error }).length > 0;
    default:
      return false;
  }
}

function shouldShowChildrenReviewFields(
  store: any,
  isArray: boolean,
  config: any,
  category: string
): boolean {
  const { editMode } = store;
  const method = isArray
    ? getIn(store.customer, `${config.name}`)
    : store.customer[category];

  return !editMode[category] && method === 'New Contact';
}

function getReviewValueForSelectWithChildren(
  getReviewFieldValue: ((store: any) => any) | undefined,
  store: any
): any {
  return getReviewFieldValue ? getReviewFieldValue(store) : '';
}

function getSelectValue(
  options: any[] | undefined,
  value: any
): any {
  const option = options?.find(
    option => option.value === value
  );
  return option ? option.label : value;
}

function clearContactFieldErrors(
  config: any,
  dispatch: (action: any) => void,
  isSelectWithChildren: boolean
) {
  if (isSelectWithChildren) {
    dispatch(
      clearContactCardError(
        config.parent.substr(
          config.parent.lastIndexOf('.') + 1
        )
      )
    );
  }
}

interface ReviewFieldProps {
  cardId: string;
  config: any;
  store: any;
  dispatch: (action: any) => void;
}

function ReviewField({
  cardId,
  config,
  store,
  dispatch
}: ReviewFieldProps): JSX.Element {
  const editMode = store.editMode[cardId];

  const {
    field,
    category,
    type,
    label,
    required,
    validations,
    addressFields,
    requiredGroupFields,
    onChecked,
    onCheckboxClicked,
    options,
    selectChildren,
    initialSelectState,
    getReviewFieldValue,
    onChange,
    onBlur,
    onAddTag,
    ...additionalProps
  } = config;

  const isAddressOrRequiredGroup =
    type === CONTROL_TYPE.address ||
    type === CONTROL_TYPE.requiredGroup;

  const isSelectWithChildren =
    type === CONTROL_TYPE.selectWithChildren;

  useEffect(() => {
    clearContactFieldErrors(
      config,
      dispatch,
      isSelectWithChildren
    );
  }, []);

  const isAttachmentList =
    type === CONTROL_TYPE.attachmentList;

  const isArray =
    config.name && config.name.match(/\[(.*?)\]/);

  const isSelect = type === CONTROL_TYPE.select;

  let name: string;
  if (isArray || isAddressOrRequiredGroup) {
    name = config.name;
  } else {
    name = config.name
      ? config.name
      : `${category}.${field}`;
  }

  const error = getIn(store.fieldError, name);
  const hasError = checkIfError(error);

  let value: any;

  if (type === CONTROL_TYPE.selectWithChildren) {
    value = getReviewValueForSelectWithChildren(
      getReviewFieldValue,
      store
    );
  } else {
    value = getValue(name, store);
  }

  if (isSelect) {
    value = getSelectValue(options, value);
  }

  const controlProps = {
    validations,
    name,
    type,
    label,
    required,
    addressFields,
    requiredGroupFields,
    options,
    selectChildren,
    initialSelectState,
    onChecked,
    onCheckboxClicked,
    onChange,
    onBlur,
    onAddTag,
    ...additionalProps
  };

  const readonlyField = (
    <Fragment>
      {!isAddressOrRequiredGroup &&
        !isAttachmentList && (
          <FieldLabel>{label || ''}</FieldLabel>
        )}

      <ReadonlyField
        type={type}
        value={value}
        requiredGroupFields={requiredGroupFields}
        addressFields={addressFields}
        label={label}
        attachments={store.attachments.saved}
        store={store}
      />

      {isSelectWithChildren &&
        shouldShowChildrenReviewFields(
          store,
          !!isArray,
          config,
          category
        ) &&
        config.selectChildren.map(
          (child: any) => (
            <ReviewField
              key={`${child.field}-review`}
              store={store}
              cardId={category}
              config={child}
              dispatch={dispatch}
            />
          )
        )}
    </Fragment>
  );

  if (editMode) {
    return (
      <Wrapper editMode={editMode} hasError={hasError}>
        <Control {...controlProps} />
      </Wrapper>
    );
  }

  return (
    <Wrapper hasError={hasError}>
      {hasError ? (
        <Control {...controlProps} />
      ) : (
        readonlyField
      )}
    </Wrapper>
  );
}

export default ReviewField;